/**
 * @module app.core
 */

import { TaskQueue, autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { INoteFlagSearchFilters, NoteService } from 'app/core/services/note-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { localSort } from 'app/shared/lib/sorting';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssTableCustomElement, ExpandEvent } from 'app/shared/elements/ess-table';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { I18N } from 'aurelia-i18n';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { deepClone } from 'app/shared/lib/utilities';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { NoteCategoryReloadEvent } from 'app/core/lib/events';
import * as Core from 'app/core/models/essette-api';

const expandedCategoriesCache = new Map<string, Array<Core.NoteCategory>>();

interface CustomNoteSubCategory extends Core.NoteSubCategory {
    flagsDisplay?: string | null;
    flagsList?: string[] | null;
}

interface CustomNoteCategory extends Core.NoteCategory {
    subCategories: CustomNoteSubCategory[];
}

@autoinject
export class NoteSubCategoriesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected expandedCacheKey = "";

    @bindable
    protected rememberExpand: boolean = true;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected notesConfig?: INotesConfig;

    protected noteCategoriesReload: Subscription | null = null;

    protected noteCategories: Core.NoteCategory[] = [];
    protected editedNoteSubCategory: Core.NoteSubCategory = {};
    private noteFlags: Core.NoteFlag[] = [];
    private sorting = new Sorting();
    protected pageSize = 10;
    protected resultCount = 0;
    protected includeInactive = false;

    protected validate = validate;
    protected isValid = false;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected captionPrefix: string | null = null;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $table!: EssTableCustomElement<Core.NoteCategory[]>;
    protected $modal!: IPopup;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: NoteService,
        private readonly queue: TaskQueue,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        // verify that component is properly configured
        if(!this.notesConfig) throw new Error('Bindable property [notes-config] was not set');
        if(!this.notesConfig?.serviceRootUrl) throw new Error('[serviceRootUrl] was not configured on bindable property [notes-config]');
        if(!this.notesConfig?.moduleCode) throw new Error('[moduleCode] was not configured on bindable property [notes-config]');
        this.expandedCacheKey = `${this.notesConfig.moduleCode.toLowerCase()}-manage-note-sub-categories`;
        this.service.configure(this.notesConfig);

        // listen for updates from the Note Categories tab
        this.noteCategoriesReload = this.events.subscribe(NoteCategoryReloadEvent, async (e: NoteCategoryReloadEvent) => {
            await this.reload();
        });
    }

    protected async attached(): Promise<void> {
        // Note [JP] Need to load in separate awaits to ensure note flags are loaded first
        await this.loadNoteFlags();
        await this.reload();
    }

    ////****************** Events ***************************************************////
    protected async handleFormSuccess(): Promise<void> {
        this.$modal.close();
        await this.reload();
    }

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
    }

    protected applyLocalSort(event: SortChangeEvent, subCategories: (Core.NoteSubCategory)[]): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(subCategories, sorting);
        if(this.sorting.entries.length == 0) {
            this.reload();
        };
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadNoteCategories();
    }

    protected saveNoteSubCategory(noteSubCategory: Core.NoteSubCategory = {}): void {
        this.editedNoteSubCategory = deepClone(noteSubCategory);
        this.$modal.open();
    }

    protected restoreExpanded() {
        if(!this.rememberExpand) { return; }
        // we need to give aurelia a chance to bind the outcomes into the
        // table before we ask the table to expand the rows
        this.queue.queueTask(() => {
            if(!this.expandedCacheKey) { return; }
            const expandedCategories = expandedCategoriesCache.get(this.expandedCacheKey);
            if(expandedCategories !== undefined) {
                this.$table.restoreExpanded(expandedCategories);
            }
        });
    }

    protected trackExpanded({ detail }: ExpandEvent<Core.NoteCategory>) {
        if(!this.rememberExpand || !this.expandedCacheKey) { return; }
        expandedCategoriesCache.set(this.expandedCacheKey, detail.items);
    }

    // Set Custom Note Sub Category Details
    private setNoteSubCategoryDetails(noteCategories: CustomNoteCategory[]): void {
        noteCategories?.forEach(noteCategory => {
            noteCategory.subCategories.forEach(noteSubCategory => {
                // Configure Flags column data
                if(!noteSubCategory?.flags || !noteSubCategory.flags.length) {
                    // if no flags, display nothing at all
                    noteSubCategory.flagsDisplay = '';
                } else if(noteSubCategory.flags.length === 1) {
                    // if only one flag, display that flag
                    noteSubCategory.flagsDisplay = noteSubCategory.flags[0].description;
                } else if(noteSubCategory.flags.length >= this.noteFlags.length) {
                    // if all flags (or somehow more), display "All Flags"
                    noteSubCategory.flagsDisplay = this.i18n.tr('ui:labels.all', { what: this.i18n.tr('core:note.flag.$', { count: 2 }) });
                } else {
                    // otherwise, display "n Flags" and generate a list for the tooltip
                    noteSubCategory.flagsDisplay = `${noteSubCategory.flags.length} ` + this.i18n.tr('core:note.flag.$', { count: 2 });
                    noteSubCategory.flagsList = noteSubCategory.flags.flatMap(f => f.description ?? '')
                        .sort((a, b) => a.localeCompare(b));
                }
            });
        });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadNoteFlags(): Promise<void> {
        const options: StandardLoadOptions = { all: true };
        const searchFilters: INoteFlagSearchFilters = { allowSubCategories: true };

        try {
            this.state.beginLoad();
            const response = await this.service.loadNoteFlags(options, [], searchFilters);
            this.noteFlags = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadNoteCategories(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting,
            includeInactive: this.includeInactive
        };

        const includes: string[] = ['subCategories', 'subCategories.flags', 'subCategories.category'];
        const withSubCategories: boolean = true;

        try {
            this.state.beginLoad();
            const response = await this.service.loadCategories(options, includes, false, withSubCategories);
            this.setNoteSubCategoryDetails(<CustomNoteCategory[]>response.results);
            this.noteCategories = response.results;
            this.resultCount = response.count || 0;
            this.restoreExpanded();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateNoteSubCategory(noteSubCategory: Core.NoteSubCategory): Promise<void> {
        if(!noteSubCategory.id) return;

        // Show confirmation dialog only if deactivating
        if(noteSubCategory.isActive) {
            const result = await confirmation.showDeactivate('core:note.category.subCategory.$long', 1);
            if(!(result.dialogResponse === confirmation.options.OK)) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateNoteSubCategory(noteSubCategory.id, !noteSubCategory.isActive);
            isSuccessful = response.isSuccessful;
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('core:note.category.subCategory.$long', !noteSubCategory.isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
